import { useEffect, useRef } from "react";

let recaptchaScriptPromise;

function loadRecaptchaExplicitScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.grecaptcha?.render) return Promise.resolve();

  if (!recaptchaScriptPromise) {
    recaptchaScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(
        'script[src*="google.com/recaptcha/api.js"]'
      );
      if (existing) {
        // Etiket zaten var; API henüz yoksa mount içinde grecaptcha.ready bekler
        resolve();
        return;
      }
      const s = document.createElement("script");
      s.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("recaptcha script load failed"));
      document.head.appendChild(s);
    });
  }
  return recaptchaScriptPromise;
}

/**
 * reCAPTCHA v2 (checkbox). react-google-recaptcha sınıf bileşeninin
 * componentDidUpdate + eksik unmount temizliği grecaptcha null.style hatalarına yol açabiliyor.
 * Bu host tek sefer render + reset ile DOM'u kontrol eder.
 */
export default function RecaptchaV2Host({
  siteKey,
  hl = "tr",
  theme = "light",
  size = "normal",
  resetSignal = 0,
  onChange,
  onExpired,
  onError,
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onExpiredRef = useRef(onExpired);
  const onErrorRef = useRef(onError);
  onChangeRef.current = onChange;
  onExpiredRef.current = onExpired;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!siteKey) return undefined;

    let cancelled = false;

    const defer = (fn) => {
      if (typeof window === "undefined") return;
      window.setTimeout(fn, 0);
    };

    const teardownWidget = () => {
      widgetIdRef.current = null;
      const el = containerRef.current;
      // DOM yarışında null.style hatasını tetiklememek için sadece bağlıysa temizle.
      if (el && el.isConnected) {
        el.innerHTML = "";
      }
    };

    const mount = () => {
      if (cancelled || !containerRef.current) return;
      const el = containerRef.current;
      el.innerHTML = "";

      window.grecaptcha.ready(() => {
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = "";
        try {
          widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token) => {
              if (!cancelled) defer(() => onChangeRef.current?.(token));
            },
            "expired-callback": () => {
              if (!cancelled) {
                defer(() => {
                  onChangeRef.current?.(null);
                  onExpiredRef.current?.();
                });
              }
            },
            "error-callback": () => {
              if (!cancelled) {
                defer(() => {
                  onChangeRef.current?.(null);
                  onErrorRef.current?.();
                });
              }
            },
            hl,
            theme,
            size,
          });
        } catch (e) {
          console.error("[RecaptchaV2Host] render failed:", e);
        }
      });
    };

    loadRecaptchaExplicitScript()
      .then(() => {
        if (!cancelled) mount();
      })
      .catch((e) => console.error("[RecaptchaV2Host] script:", e));

    return () => {
      cancelled = true;
      teardownWidget();
    };
  }, [siteKey, resetSignal, hl, theme, size]);

  if (!siteKey) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="recaptcha-v2-host"
      style={{
        minHeight: size === "compact" ? 120 : 78,
        display: "flex",
        justifyContent: "center",
      }}
    />
  );
}
